import { Link } from 'react-router-dom';
import { Package, ShoppingCart, Eye } from 'lucide-react';
import { getImageUrl } from '../lib/api';
import { useCart } from '../context/CartContext';

export const ProductCard = ({ product, view = 'grid' }) => {
  const { addToCart } = useCart();
  const hasDiscount = product.discount_rate > 0;
  const discountedPrice = product.price * (1 - product.discount_rate / 100);
  const isOutOfStock = product.quantity <= 0;

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOutOfStock) addToCart(product, 1);
  };

  /* ── Grid View Card ─────────────────────────────────────────── */
  if (view === 'grid') {
    return (
      <Link
        to={`/products/${product.id}`}
        data-testid={`product-card-${product.id}`}
        className="group bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden relative flex flex-col"
      >
        {/* Badges */}
        {hasDiscount && !isOutOfStock && (
          <div className="absolute top-3 left-3 z-10 bg-red-500 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow">
            -{product.discount_rate}% OFF
          </div>
        )}
        {isOutOfStock && (
          <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-2xl">
            <span className="bg-slate-800 text-white text-sm font-semibold px-4 py-2 rounded-full">
              Out of Stock
            </span>
          </div>
        )}

        {/* Image */}
        <div className="relative aspect-[4/3] bg-slate-50 overflow-hidden">
          {product.images && product.images.length > 0 ? (
            <img
              src={getImageUrl(product.images[0])}
              alt={product.name}
              className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-400"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Package className="w-14 h-14 text-slate-200" />
            </div>
          )}

          {/* Hover overlay — Add to Cart */}
          {!isOutOfStock && (
            <div className="absolute inset-0 bg-[#0f172a]/60 flex items-end justify-center pb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <button
                onClick={handleAddToCart}
                className="flex items-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white px-5 py-2.5 rounded-full text-sm font-semibold shadow-lg transform translate-y-3 group-hover:translate-y-0 transition-transform duration-300"
              >
                <ShoppingCart className="w-4 h-4" />
                Add to Cart
              </button>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-4 flex-1 flex flex-col">
          <p className="text-[10px] text-slate-400 uppercase tracking-widest mb-1 font-medium">
            {product.brand}
          </p>
          <h3
            className="font-semibold text-sm mb-3 line-clamp-2 text-slate-800 leading-snug flex-1"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {product.name}
          </h3>

          <div className="flex items-center justify-between mt-auto">
            {hasDiscount ? (
              <div>
                <span className="text-xs text-slate-400 line-through block">
                  KWD {product.price.toFixed(3)}
                </span>
                <span className="text-base font-bold text-red-600">
                  KWD {discountedPrice.toFixed(3)}
                </span>
              </div>
            ) : (
              <span className="text-base font-bold text-[#8acb95]">
                KWD {product.price.toFixed(3)}
              </span>
            )}

            {!isOutOfStock && (
              <span className="text-[10px] bg-green-50 text-green-700 font-semibold px-2 py-1 rounded-full border border-green-100">
                In Stock
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  /* ── List View Card ─────────────────────────────────────────── */
  return (
    <Link
      to={`/products/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="group bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden flex gap-0"
    >
      {/* Image */}
      <div className="w-36 h-36 sm:w-44 sm:h-44 bg-slate-50 flex-shrink-0 overflow-hidden relative">
        {isOutOfStock && (
          <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
            <span className="bg-slate-800 text-white text-xs font-semibold px-2 py-1 rounded-full">
              Out of Stock
            </span>
          </div>
        )}
        {product.images && product.images.length > 0 ? (
          <img
            src={getImageUrl(product.images[0])}
            alt={product.name}
            className="w-full h-full object-contain p-3 group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Package className="w-12 h-12 text-slate-200" />
          </div>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
        <div>
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-[10px] text-slate-400 uppercase tracking-widest font-medium">
              {product.brand} · {product.category}
            </p>
            {hasDiscount && !isOutOfStock && (
              <span className="flex-shrink-0 bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                -{product.discount_rate}%
              </span>
            )}
          </div>
          <h3
            className="font-semibold text-sm text-slate-800 line-clamp-2 leading-snug"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {product.name}
          </h3>
          <p className="text-xs text-slate-400 mt-1">SKU: {product.code}</p>
        </div>

        <div className="flex items-center justify-between gap-4 mt-3 flex-wrap">
          {hasDiscount ? (
            <div>
              <span className="text-xs text-slate-400 line-through">KWD {product.price.toFixed(3)}</span>
              <span className="text-lg font-bold text-red-600 block">KWD {discountedPrice.toFixed(3)}</span>
            </div>
          ) : (
            <span className="text-lg font-bold text-[#8acb95]">KWD {product.price.toFixed(3)}</span>
          )}

          {!isOutOfStock && (
            <button
              onClick={handleAddToCart}
              className="flex items-center gap-2 bg-[#8acb95] hover:bg-[#7bb885] text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors shadow-sm"
            >
              <ShoppingCart className="w-4 h-4" />
              Add to Cart
            </button>
          )}
        </div>
      </div>
    </Link>
  );
};
